import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import ReactDOM from 'react-dom';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { kpisApi } from '../../services/apiService';
import { KPIType, Frequency, Polarity } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useTranslation } from 'react-i18next';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddInitiativeKPI'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
}

const Dropdown = ({ value, options, placeholder, onChange }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBg: colors.card,
    itemActiveBg: isDark ? '#451A1A' : '#FEF2F2',
  };

  const handleOpen = () => {
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      setIsOpen(false);
    };
    const handleScroll = () => {
      setIsOpen(false);
    };
    document.addEventListener('mouseup', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        left: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        paddingVertical: 8,
        zIndex: 99999,
        elevation: 99999,
        maxHeight: 200,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
        <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.dropdownItem,
                { backgroundColor: dropdownTheme.itemBg },
                value === option.value && { backgroundColor: dropdownTheme.itemActiveBg }
              ]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[
                styles.dropdownItemText,
                { color: dropdownTheme.text },
                value === option.value && styles.dropdownItemTextActive
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
        ]}
        onPress={handleOpen}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function AddInitiativeKPIScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { initiativeId } = route.params;
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { translate, translateOrg } = useAutoTranslate();

  useEffect(() => {
    if (isViewOnly) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, navigation, navigateToHome]);

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : '#CBD5E1',
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    checkboxBorder: isDark ? '#4B5563' : '#D1D5DB',
    checkboxBg: colors.card,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    weight: '30',
    use_weight: true,
    description: '',
    kpiType: '' as KPIType | '',
    polarity: '' as Polarity | '',
    startDate: '',
    unit: '',
    frequency: '' as Frequency | '',
    formula: '',
    targetValue: '0.0',
    worstAcceptableValue: '',
    dataSource: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // KPI Type options
  const kpiTypeOptions: DropdownOption[] = [
    // 'strategic' omitted — it requires data_source_type (مستقل/تجميعي, DTS
    // 4.3), collected only on AddGoalScreen. Offering it on an operational
    // screen 400s the KPI create and silently drops it on save.
    { value: 'operational', label: t('kpis.operational') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'عدد', label: t('kpis.number') },
    { value: 'درهم', label: t('kpis.currency') },
    { value: 'يوم', label: t('kpis.day') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.kpiNameRequired');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('validation.kpiDescriptionRequired');
    }
    if (!formData.kpiType) {
      newErrors.kpiType = t('validation.kpiTypeRequired');
    }
    if (!formData.polarity) {
      newErrors.polarity = t('validation.polarityRequired');
    }
    if (!formData.unit) {
      newErrors.unit = t('validation.unitRequired');
    }
    if (!formData.frequency) {
      newErrors.frequency = t('validation.frequencyRequired');
    }
    const targetNum = parseFloat(formData.targetValue);
    const isNegativePolarity = formData.polarity === 'negative';
    const isPercentUnit = formData.unit === '%';

    if (formData.targetValue === '' || isNaN(targetNum) || targetNum < 0) {
      newErrors.targetValue = t('validation.targetValueRequired');
    } else if (!isNegativePolarity && targetNum <= 0) {
      // Positive polarity still requires > 0
      newErrors.targetValue = t('validation.targetValueRequired');
    }

    // Worst-acceptable validation (mirrors backend)
    if (isNegativePolarity && !isPercentUnit) {
      const worstStr = formData.worstAcceptableValue;
      const worstNum = worstStr === '' ? null : parseFloat(worstStr);

      if (targetNum === 0 && (worstStr === '' || worstNum === null)) {
        newErrors.worstAcceptableValue = t('kpis.worstRequired');
      } else if (worstNum !== null && !isNaN(worstNum) && worstNum <= targetNum) {
        newErrors.worstAcceptableValue = t('kpis.worstMustBeGreaterThanTarget');
      }
    }

    if (!formData.dataSource.trim()) {
      newErrors.dataSource = t('validation.dataSourceRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setLoading(true);
    try {
      const kpiData = {
        name: formData.name,
        description: formData.description,
        initiative: initiativeId,
        kpi_type: formData.kpiType as KPIType,
        polarity: formData.polarity as Polarity,
        unit: formData.unit,
        frequency: formData.frequency as Frequency,
        target_value: parseFloat(formData.targetValue).toFixed(2),
        worst_acceptable_value:
          formData.polarity === 'negative' && formData.unit !== '%' && formData.worstAcceptableValue !== ''
            ? parseFloat(formData.worstAcceptableValue).toFixed(2)
            : null,
        weight: formData.weight,
        use_weight: formData.use_weight,
        owner: formData.dataSource,
      };

      await kpisApi.create(kpiData);
      showSuccess(t('kpis.kpiAddedSuccess'));
      navigation.goBack();
    } catch (error: any) {
      console.error('Error creating KPI:', error);
      const errorMessage = error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || t('kpis.kpiAddFailed');
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: containerPadding }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('nav.home') },
            { label: t('initiatives.initiativeDetails'), route: 'InitiativeDetail', params: { id: initiativeId } },
            { label: t('kpis.addPerformanceIndicator') },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{t('kpis.addKPIForInitiative')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

            {/* KPI Name & Weight Row */}
            <View style={[styles.row, isMobile && { flexDirection: 'column' }]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={styles.setWeightButton}>
                    <Text style={styles.setWeightButtonText}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={styles.percentSymbol}>%</Text>
                  <TextInput
                    style={[styles.weightInput, !formData.use_weight && { opacity: 0.5 }]}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                    editable={formData.use_weight}
                  />
                </View>
                {/* Use Weight Toggle */}
                <TouchableOpacity
                  style={styles.useWeightToggle}
                  onPress={() => setFormData(prev => ({ ...prev, use_weight: !prev.use_weight }))}
                >
                  <View style={[styles.checkbox, formData.use_weight && styles.checkboxChecked]}>
                    {formData.use_weight && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.useWeightLabel}>{t('kpis.useWeightInCalculation')}</Text>
                </TouchableOpacity>
              </View>

              {/* KPI Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={[styles.input, errors.name && styles.inputError]}
                  value={formData.name}
                  onChangeText={(v) => updateField('name', v)}
                  placeholder=""
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>
            </View>

            {/* Description Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={styles.label}>{t('kpis.kpiDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, errors.description && styles.inputError]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>
          </View>

          {/* Classification Section */}
          <View style={[styles.section, { zIndex: 30 }]}>
            <Text style={styles.sectionTitle}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column' }]}>
              {/* Polarity Field */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={formData.polarity}
                  options={polarityOptions}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateField('polarity', v)}
                />
                {errors.polarity && <Text style={styles.errorText}>{errors.polarity}</Text>}
              </View>

              {/* KPI Type Field */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={formData.kpiType}
                  options={kpiTypeOptions}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateField('kpiType', v)}
                />
                {errors.kpiType && <Text style={styles.errorText}>{errors.kpiType}</Text>}
              </View>
            </View>
          </View>

          {/* Measurement Section */}
          <View style={[styles.section, { zIndex: 20 }]}>
            <Text style={styles.sectionTitle}>{t('kpis.measurement')}</Text>

            {/* Row 1: Start Date */}
            <View style={[styles.row, isMobile && { flexDirection: 'column' }]}>
              {/* Start Date */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.measurementStartDate')}</Text>
                </View>
                <View style={styles.dateInputContainer}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e: any) => updateField('startDate', e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        color: '#414042',
                        textAlign: 'right' as const,
                        direction: 'rtl' as const,
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color="#64748B" />
                      <TextInput
                        style={styles.dateInput}
                        value={formData.startDate}
                        onChangeText={(v) => updateField('startDate', v)}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9CA3AF"
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
              </View>
            </View>

            {/* Row 2: Unit & Frequency - HIGH z-index for dropdown */}
            <View style={[styles.row, { zIndex: 100, position: 'relative' }, isMobile && { flexDirection: 'column' }]}>
              {/* Frequency */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.measurementFrequency')}</Text>
                </View>
                <Dropdown
                  value={formData.frequency}
                  options={frequencyOptions}
                  placeholder={t('kpis.selectFrequency')}
                  onChange={(v) => updateField('frequency', v)}
                />
                {errors.frequency && <Text style={styles.errorText}>{errors.frequency}</Text>}
              </View>

              {/* Unit */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.measurementUnit')}</Text>
                </View>
                <Dropdown
                  value={formData.unit}
                  options={unitOptions}
                  placeholder={t('kpis.selectUnitHint')}
                  onChange={(v) => updateField('unit', v)}
                />
                {errors.unit && <Text style={styles.errorText}>{errors.unit}</Text>}
              </View>
            </View>

            {/* Formula Field - lower z-index */}
            <View style={[styles.field, { zIndex: 1 }]}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={styles.label}>{t('kpis.measurementFormula')}</Text>
              </View>
              <TextInput
                style={styles.input}
                value={formData.formula}
                onChangeText={(v) => updateField('formula', v)}
                placeholder={t('kpis.formulaPlaceholder')}
                placeholderTextColor="#9CA3AF"
                textAlign="right"
              />
            </View>

            {/* Row 3: Target Value & Data Source - lowest z-index */}
            <View style={[styles.row, { zIndex: 1 }, isMobile && { flexDirection: 'column' }]}>
              {/* Data Source */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.dataSource')}</Text>
                </View>
                <TextInput
                  style={[styles.input, errors.dataSource && styles.inputError]}
                  value={formData.dataSource}
                  onChangeText={(v) => updateField('dataSource', v)}
                  placeholder={t('kpis.dataSourcePlaceholder')}
                  placeholderTextColor="#9CA3AF"
                  textAlign="right"
                />
                {errors.dataSource && <Text style={styles.errorText}>{errors.dataSource}</Text>}
              </View>

              {/* Target Value */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.targetValue')}</Text>
                </View>
                <TextInput
                  style={[styles.input, errors.targetValue && styles.inputError]}
                  value={formData.targetValue}
                  onChangeText={(v) => updateField('targetValue', normalizeArabicNumbers(v))}
                  keyboardType="numeric"
                  textAlign="right"
                />
                {errors.targetValue && <Text style={styles.errorText}>{errors.targetValue}</Text>}
              </View>
            </View>

            {/* Worst Acceptable Value — only for negative polarity + non-% unit */}
            {formData.polarity === 'negative' && formData.unit && formData.unit !== '%' && (
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  {parseFloat(formData.targetValue) === 0 && (
                    <Text style={styles.requiredStar}>*</Text>
                  )}
                  <Text style={styles.label}>{t('kpis.worstAcceptableValue')}</Text>
                </View>
                <TextInput
                  style={[styles.input, errors.worstAcceptableValue && styles.inputError]}
                  value={formData.worstAcceptableValue}
                  onChangeText={(v) => updateField('worstAcceptableValue', normalizeArabicNumbers(v))}
                  keyboardType="numeric"
                  textAlign="right"
                  placeholder={t('kpis.worstAcceptableValueHint')}
                  placeholderTextColor="#9CA3AF"
                />
                {errors.worstAcceptableValue && <Text style={styles.errorText}>{errors.worstAcceptableValue}</Text>}
              </View>
            )}
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.saveButton, loading && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{t('common.save')}</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
              onPress={handleCancel}
              disabled={loading}
            >
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },
  // Section
  section: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  // Field styles
  field: {
    marginBottom: 16,
  },
  fieldSmall: {
    width: '30%',
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#EF4444',
  },
  // Input styles
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    minHeight: 44,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  setWeightButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  setWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Use Weight Toggle
  useWeightToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: '#A9211F',
    borderColor: '#A9211F',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  useWeightLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    gap: 8,
  },
  dateInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1,
  },
  dropdownContainerOpen: {
    zIndex: 9999,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  dropdownButtonOpen: {
    borderColor: '#A9211F',
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownPlaceholder: {
    color: '#9CA3AF',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    zIndex: 99999,
    maxHeight: 200,
    overflow: 'hidden',
  },
  dropdownScroll: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  dropdownItemActive: {
    backgroundColor: '#FEF2F2',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItemTextActive: {
    color: '#A9211F',
    fontWeight: '600',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Error styles
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
});
